import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import { Button } from '../components/ui/button';
import { ArrowRight } from 'lucide-react';
import fullyHumanHero from '@/assets/fully-human-hero.jpg';
import fullyHumanContext from '@/assets/fully-human-context.jpg';
import fullyHumanCta from '@/assets/fully-human-cta.jpg';

const FullyHuman = () => {
  const handleScheduleCall = () => {
    window.open("https://calendly.com/kentfrazier/30-minutes", "_blank");
  };

  const monthlyThemes = [
    // Act 1: Ground - Wake up to what's here
    { name: 'Attention', question: 'What am I actually giving my presence to?' },
    { name: 'Identity', question: 'Who am I when the old story no longer holds?' },
    { name: 'Shadow', question: 'What am I refusing to feel?' },
    { name: 'Embodiment', question: 'Where is this living in my body, beyond my mind?' },
    // Act 2: Engage - Stay awake with others
    { name: 'Courage', question: 'What am I avoiding that matters?' },
    { name: 'Relationship', question: 'Who can I be honest with, really?' },
    { name: 'Trust', question: 'What can I still trust when so much has broken?' },
    { name: 'Power', question: 'What does responsible influence look like now?' },
    // Act 3: Extend - Build from what remains
    { name: 'Integrity', question: 'Where am I still pretending things are fine?' },
    { name: 'Belonging', question: 'Where do I find real community in the wreckage?' },
    { name: 'Systems', question: 'What are we building to replace what\'s falling?' },
    { name: 'Legacy', question: 'What do we carry forward from here?' },
  ];

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": "Fully Human",
    "description": "A year-long practice of presence, depth, and integrity in the age of artificial everything.",
    "url": "https://www.paradoxedge.com/fully-human",
    "provider": { "@type": "Organization", "name": "ParadoxEdge", "url": "https://www.paradoxedge.com" },
  };

  return (
    <>
      <SEOHead
        noindex
        title="Fully Human - Presence in the Age of AI"
        description="A 6-week live cohort for leaders who refuse to lose depth. Practice attention, integrity, and relationship in a world accelerating past what matters."
        canonicalUrl="/fully-human"
        keywords="leadership development, presence practice, executive coaching, fully human, depth, integrity, attention"
        jsonLd={jsonLd}
      />
      <div className="min-h-screen bg-background">
        <Navigation />
        <main>

          {/* ═══ HERO - with background image ═══ */}
          <div className="flex flex-col min-h-[calc(100vh-5rem)]">
            <section className="relative flex-1 flex items-center justify-center overflow-hidden">
              <div 
                className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                style={{ backgroundImage: `url(${fullyHumanHero})` }}
              />
              <div className="absolute inset-0 bg-foreground/85" />

              <div className="container-mobile relative z-10">
                <div className="max-w-4xl mx-auto text-center space-y-10">
                  <p className="text-primary font-semibold tracking-[0.2em] uppercase text-sm drop-shadow-lg">
                    A Practice of Becoming
                  </p>

                  <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-display text-background leading-[1.1] drop-shadow-lg">
                    Fully Human
                  </h1>

                  <p className="text-xl sm:text-2xl text-primary max-w-3xl mx-auto leading-relaxed font-display italic" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.7)' }}>
                    Developing our humanity in the age of artificial everything.
                  </p>

                  <div className="pt-6 flex justify-center">
                    <Button
                      onClick={handleScheduleCall}
                      size="lg"
                      className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold text-lg px-10 py-7 rounded-full transition-all duration-300 hover:shadow-xl hover:shadow-primary/20 hover:scale-105 group"
                    >
                      Begin a Conversation
                      <ArrowRight className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </div>
                </div>
              </div>
            </section>

            {/* ═══ SAGE BANNER ═══ */}
            <section className="bg-sage py-5">
              <div className="container-mobile">
                <p className="text-center text-sage-foreground text-sm sm:text-base font-display font-semibold italic tracking-wide">
                  You already know this matters.
                </p>
              </div>
            </section>
          </div>

          {/* ═══ THE CONTEXT - dark charcoal with side image ═══ */}
          <section className="relative py-28 sm:py-36 overflow-hidden">
            <div className="absolute inset-0 bg-foreground" />
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute -top-20 right-[10%] w-72 h-72 bg-primary/8 rounded-full blur-3xl" />
            </div>

            <div className="container-mobile relative z-10">
              <div className="max-w-5xl mx-auto">
                <div className="grid lg:grid-cols-[1fr_auto] gap-16 items-center">
                  <div className="max-w-3xl">
                    <p className="text-primary font-semibold tracking-[0.2em] uppercase text-xs mb-6">
                      The Context
                    </p>

                    <h2 className="text-background text-3xl sm:text-4xl lg:text-5xl mb-14 leading-tight">
                      Something Essential<br />
                      <span className="text-primary">Is Being Lost.</span>
                    </h2>

                    <div className="space-y-6 text-background/70 text-lg sm:text-xl leading-relaxed max-w-2xl">
                      <p>Institutions we trusted are failing.</p>
                      <p>Shared truth is fracturing.</p>
                      <p>The pace of change has outrun our capacity to process it.</p>
                      <p className="text-background/85 font-medium pt-4">When we can't grieve, we rage.<br />When we can't rage, we numb.<br />When we numb long enough, we forget what we cared about.</p>
                    </div>

                    <div className="w-12 h-[2px] bg-primary my-14" />

                    <p className="text-background/80 text-lg sm:text-xl leading-relaxed max-w-2xl">
                      Most people cope by speeding up, checking out, or performing certainty they don't feel.
                    </p>

                    <p className="text-background text-2xl sm:text-3xl font-display leading-snug mt-12">
                      Fully Human is an invitation to<br className="hidden sm:block" />
                      <span className="text-primary"> remember and reimagine our humanity.</span>
                    </p>
                  </div>

                  <div className="hidden lg:block w-64 xl:w-80">
                    <img 
                      src={fullyHumanContext} 
                      alt="A balanced stone cairn in warm light, symbolizing intentional practice and grounding"
                      className="rounded-2xl shadow-2xl shadow-primary/10 w-full"
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ═══ THE OFFER - cream ═══ */}
          <section className="relative py-28 sm:py-36 overflow-hidden bg-background">
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute top-10 left-[20%] w-64 h-64 bg-primary/6 rounded-full blur-3xl" />
            </div>

            <div className="container-mobile relative z-10">
              <div className="max-w-4xl mx-auto">
                <div className="grid md:grid-cols-[1fr_1fr] gap-16 items-start">
                  <div>
                    <p className="text-primary font-semibold tracking-[0.2em] uppercase text-xs mb-6">
                      The Offer
                    </p>
                    <h2 className="text-foreground text-3xl sm:text-4xl lg:text-5xl mb-8 leading-tight">
                      A Year-Long<br />Practice.
                    </h2>
                    <p className="text-foreground/90 text-lg leading-relaxed mb-10">
                      A year-long developmental commitment for human leaders navigating an increasingly artificial world.
                    </p>
                    <div className="hidden md:block">
                      <div className="w-12 h-[2px] bg-primary mb-6" />
                      <p className="text-foreground/50 text-sm font-semibold tracking-[0.15em] uppercase mb-4">
                        Presence drives performance.
                      </p>
                      <p className="text-foreground font-display text-2xl leading-snug">
                        Twelve months of becoming<br />more fully human.
                      </p>
                    </div>
                  </div>

                  <div className="space-y-0">
                    {[
                      { label: '12 Months', detail: 'One theme per month, building on the last' },
                      { label: 'Bi-Weekly Rhythm', detail: 'Full group one week, triads the next' },
                      { label: '12 People', detail: 'Small cohort, high trust' },
                      { label: 'Live Only', detail: 'No recordings, full presence' },
                    ].map((item, i) => (
                      <div key={i} className="border-b border-foreground/10 last:border-b-0 py-6 first:pt-0">
                        <p className="text-foreground font-display text-xl mb-1">{item.label}</p>
                        <p className="text-foreground/85 text-sm">{item.detail}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="md:hidden mt-14 text-center">
                  <div className="w-12 h-[2px] bg-primary mx-auto mb-6" />
                  <p className="text-foreground/50 text-sm font-semibold tracking-[0.15em] uppercase mb-4">
                    Presence drives performance.
                  </p>
                  <p className="text-foreground font-display text-2xl leading-snug">
                    Twelve months of becoming<br />more fully human.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* ═══ THE ARC - dark charcoal with warm cards ═══ */}
          <section className="relative py-28 sm:py-36 overflow-hidden">
            <div className="absolute inset-0 bg-foreground" />
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute top-20 left-[10%] w-96 h-96 bg-primary/8 rounded-full blur-3xl" />
              <div className="absolute bottom-20 right-[5%] w-72 h-72 bg-primary/5 rounded-full blur-3xl" />
            </div>

            <div className="container-mobile relative z-10">
              <div className="max-w-5xl mx-auto">
                <div className="text-center mb-16">
                  <p className="text-primary font-semibold tracking-[0.2em] uppercase text-xs mb-6">
                    The Arc
                  </p>
                  <h2 className="text-background text-3xl sm:text-4xl lg:text-5xl leading-tight">
                    Twelve Months.<br />
                    <span className="text-primary">Twelve Topics.</span>
                  </h2>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 mb-16">
                  {monthlyThemes.map((theme, i) => (
                    <div
                      key={theme.name}
                      className="group relative p-6 sm:p-8 rounded-xl border border-background/15 hover:border-primary/40 transition-all duration-500 hover:shadow-lg hover:shadow-primary/10"
                      style={{ backgroundColor: 'hsl(40 25% 92% / 0.08)' }}
                    >
                      <p className="text-primary/70 text-xs font-semibold tracking-[0.15em] uppercase mb-2">
                        Month {i + 1}
                      </p>
                      <p className="text-background group-hover:text-primary font-display text-xl sm:text-2xl transition-colors duration-500 mb-2">
                        {theme.name}
                      </p>
                      <p className="text-background/65 text-sm italic leading-relaxed">
                        {theme.question}
                      </p>
                    </div>
                  ))}
                </div>

                <div className="text-center">
                  <p className="text-background/70 text-lg font-medium">
                    Each month builds on the last. The arc is the practice.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* ═══ WHO THIS IS FOR - warm card tone ═══ */}
          <section className="relative py-28 sm:py-36 overflow-hidden bg-card">
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute top-10 right-[15%] w-64 h-64 bg-primary/5 rounded-full blur-3xl" />
            </div>

            <div className="container-mobile relative z-10">
              <div className="max-w-4xl mx-auto">
                <div className="grid md:grid-cols-[1fr_1.2fr] gap-16 items-start">
                  <div>
                    <p className="text-primary font-semibold tracking-[0.2em] uppercase text-xs mb-6">
                      Who This Is For
                    </p>
                    <div className="space-y-3 text-foreground text-3xl sm:text-4xl font-display leading-snug">
                      <p>Leaders.</p>
                      <p>Parents.</p>
                      <p>Founders.</p>
                      <p>Creators.</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="border-l-2 border-primary/30 pl-8 sm:pl-10">
                      <p className="text-foreground/90 text-xl sm:text-2xl font-display leading-relaxed">
                        Professionals who feel the speed of the world
                        and refuse to lose their soul.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ═══ FINAL CTA - with background image ═══ */}
          <section className="relative py-28 sm:py-36 overflow-hidden">
            <div 
              className="absolute inset-0 bg-cover bg-center bg-no-repeat"
              style={{ backgroundImage: `url(${fullyHumanCta})` }}
            />
            <div className="absolute inset-0 bg-foreground/85" />
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute bottom-10 left-[15%] w-64 h-64 bg-primary/6 rounded-full blur-3xl" />
            </div>

            <div className="container-mobile relative z-10">
              <div className="max-w-3xl mx-auto text-center space-y-10">
                <div className="w-12 h-[2px] bg-primary mx-auto" />

                <p className="text-primary font-semibold tracking-[0.2em] uppercase text-xs">
                  The Invitation
                </p>

                <h2 className="text-background text-3xl sm:text-4xl lg:text-5xl leading-tight drop-shadow-lg">
                  Ready to Walk<br />
                  <span className="text-primary">Your Talk?</span>
                </h2>

                <p className="text-background/70 text-lg sm:text-xl leading-relaxed max-w-lg mx-auto drop-shadow-lg">
                  Schedule a conversation to talk about what you're navigating
                  and whether this year-long practice is the right container.
                </p>

                <div className="pt-4">
                  <Button
                    onClick={handleScheduleCall}
                    size="lg"
                    className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold text-base px-10 py-7 rounded-full transition-all duration-300 hover:shadow-xl hover:shadow-primary/20 hover:scale-105 group"
                  >
                    Begin a Conversation
                    <ArrowRight className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </div>

                <p className="text-background/45 text-sm tracking-[0.2em] uppercase drop-shadow-lg">
                  Limited seats · Free 30-minute call
                </p>
              </div>
            </div>
          </section>

        </main>
        <Footer />
      </div>
    </>
  );
};

export default FullyHuman;
